import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { TspBreadcrumb } from "../../components/primitives";
import TspLoadError from "../../components/common/TspLoadError";
import useTspPermission from "../../hooks/useTspPermission";
import TSP_PERMISSIONS from "../../constants/tspPermissions";
import notification from "../../../../helpers/notification";
import useTspAddJourneyData from "./useTspAddJourneyData";
import useTspAddJourneyForm from "./useTspAddJourneyForm";
import { JOURNEY_RAIL_SECTIONS } from "../../constants/tspJourneyRail";
import useTspJourneyMapPrefill from "./useTspJourneyMapPrefill";
import { GEOCODE_STATE, mapSelectionForForm } from "./mapPickModel";
import AddJourneyMapNotice from "./components/AddJourneyMapNotice";
import AddJourneyActions from "./components/AddJourneyActions";
import JourneySectionNav from "./components/JourneySectionNav";
import JourneyRoutePlanCard from "./components/JourneyRoutePlanCard";
import AddJourneySaveProgress from "./components/add-journey-city/AddJourneySaveProgress";
import JourneyPlaceAdder from "./builder/JourneyPlaceAdder";
import JourneyPlaceList from "./builder/JourneyPlaceList";
import JourneyLeaderField from "./builder/JourneyLeaderField";
import { NO_TEMPLE_MESSAGE } from "./journeyForm";
import stagedPlaceStore from "./builder/stagedPlaceStore";
import TspApiService from "../../api/tspApiService";
import useTspApiResource from "../../hooks/useTspApiResource";
import TspBackButton from "../../components/common/TspBackButton";

const NOTHING = () => Promise.resolve({ data: null });

// The right rail is the same Page Sections rail as Your Stats (JourneySectionNav),
// with the same sections. There is no journey yet, so each section links to its
// own screen: Your Stats (the user's current journey) or the Journeys screen.
const RAIL_PAGE_ROUTES = Object.freeze({ journey: "/tsp/journeys", stats: "/tsp/stats" });

// Add Journey (/tsp/journeys/new) — the Journey Builder in create mode: add
// places one by one, each with its visit date (the Add Journey Place card, which
// also holds the optional Team Leader) -> each added place appears as a card
// above it -> fill each place's sections on its own card -> Create Journey. Everything stays on this
// page. Saves through the TSP API; once the server confirms, it opens the
// journey that was created — already In Progress (creating a journey starts
// it). The temple is the signed-in user's own; Start / End Date are the first
// and last place dates.
//
// Two ways in, one form and one API: from Journeys -> "Create New Journey"
// (empty), or from a point picked on the map (/tsp/journeys/new?lat=&lng=),
// which pre-fills the place picker with that place, its coordinates and areas —
// all still editable here, and all validated again by the server on save.
export default function TspAddJourneyPage() {
  const navigate = useNavigate();
  const { data, loading, error, reload } = useTspAddJourneyData();
  const { hasPermission, loading: permissionsLoading } = useTspPermission();
  const canManage = hasPermission(TSP_PERMISSIONS.JOURNEYS_MANAGE);
  const form = useTspAddJourneyForm({ canManage, temples: data?.temples, onCreated: (journey) => navigate(`/tsp/journeys/${journey.id}/edit`, { replace: true, state: { autoOpenFirst: true } }) });
  const mapPrefill = useTspJourneyMapPrefill({ enabled: canManage });
  // What each place's sections may record (the server checks every one again).
  const perms = {
    books: hasPermission(TSP_PERMISSIONS.BOOK_DISTRIBUTION_MANAGE),
    accommodation: hasPermission(TSP_PERMISSIONS.ACCOMMODATION_MANAGE),
    media: hasPermission(TSP_PERMISSIONS.MEDIA_UPLOAD),
    advice: hasPermission(TSP_PERMISSIONS.ADVICE_MANAGE),
    response: hasPermission(TSP_PERMISSIONS.PUBLIC_RESPONSE_MANAGE),
  };
  perms.mediaVisible = perms.media;
  perms.adviceVisible = perms.advice;
  perms.tabs = [
    perms.books && "books",
    perms.accommodation && "accommodation",
    (perms.media || perms.advice) && "mediaAdvice",
    perms.response && "response",
  ].filter(Boolean);

  // The book catalog: loaded the first time a Book Distribution form is opened
  // (by someone who can record it), then kept.
  const [catalogRequested, setCatalogRequested] = useState(false);
  const catalogWanted = perms.books && catalogRequested;
  const catalog = useTspApiResource(() => (catalogWanted ? TspApiService.getBookCatalog() : NOTHING()), [catalogWanted]);
  const requestCatalog = useCallback(() => setCatalogRequested(true), []);
  const bookCatalog = { ...catalog, request: requestCatalog };

  // Pre-fill once per settled answer for the point (a failed lookup that is
  // retried and then resolves pre-fills again, replacing the bare point).
  const appliedPrefill = useRef("");
  const { point: mapPoint } = mapPrefill;
  const { state: mapState, data: mapData } = mapPrefill.geocode;
  const { applyMapSelection } = form;
  useEffect(() => {
    if (!mapPoint || [GEOCODE_STATE.idle, GEOCODE_STATE.loading, GEOCODE_STATE.invalid, GEOCODE_STATE.forbidden].includes(mapState)) return;
    const key = `${mapPoint.latitude},${mapPoint.longitude}:${mapState}`;
    if (appliedPrefill.current === key) return;
    appliedPrefill.current = key;
    applyMapSelection(mapSelectionForForm(mapPoint, mapData));
  }, [mapPoint, mapState, mapData, applyMapSelection]);

  if (loading) return <div className="tsp-page tsp-dashboard-loading">Loading…</div>;
  if (error || !data) return <TspLoadError error={error} onRetry={reload} what="this form" />;

  // Locked while saving, and for good once the journey exists (it is never created twice).
  const busy = Boolean(form.submitting) || Boolean(form.detailsSave);
  const places = form.values.cities.map((stop) => ({ ...stop, key: stop.id }));

  const handleCancel = () => {
    if (form.isDirty && !window.confirm("Discard this journey? Your entries will be lost.")) return;
    navigate("/tsp/journeys");
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    form.submit();
  };

  // Each card's own state and actions; its sections are held in the browser
  // until Create Journey (stagedPlaceStore over this place's details).
  const cardProps = (place, index) => ({
    expanded: Boolean(form.openCards[place.id]),
    onToggle: () => form.toggleCard(place.id),
    activeTab: form.activeTabs[place.id] || null,
    onTabChange: (tab) => form.setCardTab(place.id, tab),
    usePlaceStore: (current) => stagedPlaceStore({ details: form.detailsOf(current.id), onChange: (updater) => form.changeCityDetails(current.id, updater), range: form.range }),
    perms,
    range: form.range,
    bookCatalog,
    canEditRoute: true,
    onDateChange: (date) => form.setPlaceDate(place.id, date),
    onStatusChange: (currentPlace, status) => {
      const isVisited = status === "completed";
      const isCurrent = status === "live";
      const isUpcoming = status === "not_visited";
      form.setPlaceVisitState?.(currentPlace.id, isVisited ? "visited" : isCurrent ? "current" : "not_visited");
      if (isVisited || isCurrent) {
        form.changeCityDetails(currentPlace.id, (cur) => ({
          ...cur,
          visit: {
            ...cur.visit,
            enabled: true,
            date: currentPlace.plannedDate || new Date().toISOString().slice(0, 10),
            remarks: cur.visit?.remarks || `Status set to ${isCurrent ? "In Progress" : "Completed"}`,
          },
        }));
      } else if (isUpcoming) {
        form.changeCityDetails(currentPlace.id, (cur) => ({
          ...cur,
          visit: {
            ...cur.visit,
            enabled: false,
          },
        }));
      }
      notification("success", `Status updated to ${isCurrent ? "In Progress" : isVisited ? "Completed" : "Upcoming"}.`);
    },
    onRemove: () => form.removeCity(place.id),
    onMove: (direction) => form.moveCity(index, direction),
    onChangePlace: form.pendingMapPoint ? null : () => form.editStop(place.id),
    tabErrors: form.detailErrors[place.id] || {},
    dateError: form.errors.placeDates ? form.errors.placeDates[place.id] : "",
    disabled: busy,
  });

  return (
    <div className="tsp-page tsp-add-journey-page tsp-journey-builder">
      <div className="tsp-page-heading-row">
        <div>
          <h1>Add Journey</h1>
          <TspBreadcrumb items={data.breadcrumb} />
        </div>
        <TspBackButton fallback="/tsp/journeys" />
      </div>

      <AddJourneyMapNotice prefill={mapPrefill} />

      {form.submitError && (
        <div className="tsp-form-error" role="alert">
          {form.submitError}
        </div>
      )}

      <form className="tsp-journey-detail-layout" onSubmit={handleSubmit} noValidate aria-busy={busy}>
        <div className="tsp-journey-detail-main">
          <JourneyRoutePlanCard places={places} />
          <JourneyPlaceList places={places} cardProps={cardProps} />
          <JourneyPlaceAdder
            states={data.states}
            onAdd={form.addCity}
            error={form.errors.cities}
            disabled={busy}
            preset={form.pickerPreset}
            placeCount={places.length}
            notice={
              (data.temples || []).length === 0 ? (
                <p className="tsp-form-error" role="alert" id="tsp-journey-temple">
                  {NO_TEMPLE_MESSAGE}
                </p>
              ) : null
            }
            leader={<JourneyLeaderField value={{ name: form.values.leaderName, userId: form.values.leaderUserId }} error={form.errors.leader} onChange={form.setLeader} disabled={busy} />}
          />
        </div>

        <JourneySectionNav sections={JOURNEY_RAIL_SECTIONS} currentPage="add" pageRoutes={RAIL_PAGE_ROUTES}>
          <AddJourneyActions canManage={canManage} permissionsLoading={permissionsLoading} submitting={form.submitting} onCancel={handleCancel} />
        </JourneySectionNav>
      </form>

      {form.detailsSave && <AddJourneySaveProgress save={form.detailsSave} onRetry={form.retryDetails} onOpenJourney={form.openCreatedJourney} />}

      <div className="tsp-sr-only" aria-live="polite" role="status">
        {form.announcement}
      </div>
      <ToastContainer />
    </div>
  );
}
